import { create } from 'zustand';

import { EventUnionWithPermissions } from '../../types/events.types';

// A queued create has no server row during the pending window (hybrid design), so its skeleton
// lives here and is merged on top of the fetched list by useEvents until the real event arrives
// (reservedId appears in the server list) or the entry ages out.
export type PendingCreateEvent = {
  companyId: string;
  createdAtMs: number;
  event: EventUnionWithPermissions;
  // Fires once when the queued create settles: the reserved id lands in the polled list
  // (APPLIED/FAILED per its lifecycle status) or the TTL elapses (EXPIRED, silent) — the
  // create toast belongs here so it matches the skeleton leaving the list.
  onApplySettled?: (outcome: PendingMutationSettleOutcome) => void;
  reservedId?: string;
  tempId: string;
};

export type PendingCreateSettlement = {
  outcome: PendingMutationSettleOutcome;
  tempId: string;
};

// After this long an unreconciled placeholder is dropped, so a failed or append-merged create
// (fundraising/issuance) never leaves a skeleton stuck on screen.
export const PENDING_CREATE_TTL_MS = 60_000;

export enum PendingMutationKind {
  DELETE = 'delete',
  EDIT = 'edit',
}

// How a queued edit/delete left the pending window: the consumer applied it, the apply failed
// (row wears the FAILED badge), or the TTL elapsed without an observable settle (silent).
export enum PendingMutationSettleOutcome {
  APPLIED = 'applied',
  EXPIRED = 'expired',
  FAILED = 'failed',
}

export type PendingMutationSettlement = {
  eventId: string;
  outcome: PendingMutationSettleOutcome;
};

// An edit/delete is applied asynchronously (EVENT_MUTATION pipeline): the request resolves
// before the consumer applies the mutation, and if the apply wins the race with the next list
// fetch the server never exposes a pending lifecycle status. Tracking the eventId here keeps
// the list polling until a fetch made after the request settles it (see useEvents), so the
// derived queries (snapshots/graph, valuation, transactions) are re-invalidated post-apply and
// `onApplySettled` (success/failure toast at the call site) fires when the mutation actually
// lands — not when the HTTP request resolves.
export type PendingEditEvent = {
  companyId: string;
  // Set once the request is acknowledged; settle detection ignores fetches older than this so
  // a list read racing the enqueue cannot claim the mutation already applied.
  enqueuedAtMs?: number;
  eventId: string;
  onApplySettled?: (outcome: PendingMutationSettleOutcome) => void;
  requestedAtMs: number;
};

export type PendingDeleteEvent = PendingEditEvent;

export const PENDING_EDIT_TTL_MS = 60_000;

type PendingEventsStoreState = {
  addPendingCreate: (pending: PendingCreateEvent) => void;
  addPendingDelete: (pending: PendingDeleteEvent) => void;
  addPendingEdit: (pending: PendingEditEvent) => void;
  markPendingDeleteEnqueued: (eventId: string) => void;
  markPendingEditEnqueued: (eventId: string) => void;
  pendingByCompany: Record<string, PendingCreateEvent[]>;
  pendingDeletesByCompany: Record<string, PendingDeleteEvent[]>;
  pendingEditsByCompany: Record<string, PendingEditEvent[]>;
  removePendingCreate: (tempId: string) => void;
  removePendingDelete: (eventId: string) => void;
  removePendingEdit: (eventId: string) => void;
  setReservedId: (tempId: string, reservedId: string) => void;
  settlePendingCreates: (companyId: string, settlements: PendingCreateSettlement[]) => void;
  settlePendingDeletes: (companyId: string, settlements: PendingMutationSettlement[]) => void;
  settlePendingEdits: (companyId: string, settlements: PendingMutationSettlement[]) => void;
};

const withoutTempId = (list: PendingCreateEvent[], tempId: string): PendingCreateEvent[] =>
  list.filter((pending) => pending.tempId !== tempId);

const withoutEventId = (
  byCompany: Record<string, PendingEditEvent[]>,
  eventId: string,
): Record<string, PendingEditEvent[]> =>
  Object.fromEntries(
    Object.entries(byCompany).map(([companyId, list]) => [
      companyId,
      list.filter((pending) => pending.eventId !== eventId),
    ]),
  );

const withEnqueuedAt = (
  byCompany: Record<string, PendingEditEvent[]>,
  eventId: string,
): Record<string, PendingEditEvent[]> =>
  Object.fromEntries(
    Object.entries(byCompany).map(([companyId, list]) => [
      companyId,
      list.map((pending) =>
        pending.eventId === eventId ? { ...pending, enqueuedAtMs: Date.now() } : pending,
      ),
    ]),
  );

export const pendingEventsStore = create<PendingEventsStoreState>()((set, get) => {
  // Removes the settled entries first, then fires their callbacks — a second observer (another
  // mounted useEvents instance detecting the same settle) finds nothing left to settle, so a
  // call-site toast can never fire twice for one mutation.
  const settle = (
    key: 'pendingDeletesByCompany' | 'pendingEditsByCompany',
    companyId: string,
    settlements: PendingMutationSettlement[],
  ): void => {
    const outcomeByEventId = new Map(settlements.map(({ eventId, outcome }) => [eventId, outcome]));
    const settled = (get()[key][companyId] ?? []).filter((pending) =>
      outcomeByEventId.has(pending.eventId),
    );
    if (settled.length === 0) return;

    const remainingByCompany = {
      ...get()[key],
      [companyId]: (get()[key][companyId] ?? []).filter(
        (pending) => !outcomeByEventId.has(pending.eventId),
      ),
    };
    if (key === 'pendingEditsByCompany') {
      set({ pendingEditsByCompany: remainingByCompany });
    } else {
      set({ pendingDeletesByCompany: remainingByCompany });
    }

    settled.forEach((pending) => {
      const outcome = outcomeByEventId.get(pending.eventId);
      if (outcome) pending.onApplySettled?.(outcome);
    });
  };

  return {
    pendingByCompany: {},
    pendingDeletesByCompany: {},
    pendingEditsByCompany: {},

    addPendingCreate: (pending) =>
      set((state) => ({
        pendingByCompany: {
          ...state.pendingByCompany,
          [pending.companyId]: [pending, ...(state.pendingByCompany[pending.companyId] ?? [])],
        },
      })),

    addPendingDelete: (pending) =>
      set((state) => ({
        pendingDeletesByCompany: {
          ...state.pendingDeletesByCompany,
          [pending.companyId]: [
            pending,
            ...(state.pendingDeletesByCompany[pending.companyId] ?? []).filter(
              (existing) => existing.eventId !== pending.eventId,
            ),
          ],
        },
      })),

    addPendingEdit: (pending) =>
      set((state) => ({
        pendingEditsByCompany: {
          ...state.pendingEditsByCompany,
          [pending.companyId]: [
            pending,
            ...(state.pendingEditsByCompany[pending.companyId] ?? []).filter(
              (existing) => existing.eventId !== pending.eventId,
            ),
          ],
        },
      })),

    markPendingDeleteEnqueued: (eventId) =>
      set((state) => ({
        pendingDeletesByCompany: withEnqueuedAt(state.pendingDeletesByCompany, eventId),
      })),

    markPendingEditEnqueued: (eventId) =>
      set((state) => ({
        pendingEditsByCompany: withEnqueuedAt(state.pendingEditsByCompany, eventId),
      })),

    removePendingDelete: (eventId) =>
      set((state) => ({
        pendingDeletesByCompany: withoutEventId(state.pendingDeletesByCompany, eventId),
      })),

    removePendingEdit: (eventId) =>
      set((state) => ({
        pendingEditsByCompany: withoutEventId(state.pendingEditsByCompany, eventId),
      })),

    settlePendingDeletes: (companyId, settlements) =>
      settle('pendingDeletesByCompany', companyId, settlements),

    settlePendingEdits: (companyId, settlements) =>
      settle('pendingEditsByCompany', companyId, settlements),

    removePendingCreate: (tempId) =>
      set((state) => ({
        pendingByCompany: Object.fromEntries(
          Object.entries(state.pendingByCompany).map(([companyId, list]) => [
            companyId,
            withoutTempId(list, tempId),
          ]),
        ),
      })),

    setReservedId: (tempId, reservedId) =>
      set((state) => ({
        pendingByCompany: Object.fromEntries(
          Object.entries(state.pendingByCompany).map(([companyId, list]) => [
            companyId,
            list.map((pending) =>
              pending.tempId === tempId ? { ...pending, reservedId } : pending,
            ),
          ]),
        ),
      })),

    // Same remove-before-fire invariant as `settle`: a second observer of the same settle finds
    // nothing left, so the create toast can never fire twice for one queued create.
    settlePendingCreates: (companyId, settlements) => {
      const outcomeByTempId = new Map(settlements.map(({ outcome, tempId }) => [tempId, outcome]));
      const settled = (get().pendingByCompany[companyId] ?? []).filter((pending) =>
        outcomeByTempId.has(pending.tempId),
      );
      if (settled.length === 0) return;

      set((state) => ({
        pendingByCompany: {
          ...state.pendingByCompany,
          [companyId]: (state.pendingByCompany[companyId] ?? []).filter(
            (pending) => !outcomeByTempId.has(pending.tempId),
          ),
        },
      }));

      settled.forEach((pending) => {
        const outcome = outcomeByTempId.get(pending.tempId);
        if (outcome) pending.onApplySettled?.(outcome);
      });
    },
  };
});
